import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { captureEvent } from '../../../../services/event-capture';
import { useAppDispatch } from '../../../../services/store';
import {
  fetchVesselsForCurrentMember,
  selectIsSelectedLoggedOnVesselInvalid,
  selectSelectAllVessel,
  selectSelectedVessel,
  selectedFloatPlanLoggedOn,
  setSelectedVessel,
} from '../../../../services/store/modules/vessels';
import { Vessel } from '../../../../services/store/modules/vessels/models';

type UseVesselReturnType = {
  vessels: Vessel[];
  initialLoad: boolean;
  isSelectedLoggedOnVesselInvalid: boolean;
  isLoggedOn: boolean;
  selectedVessel: Vessel | undefined;
  fetchVessels: () => void;
  selectVessel: (vessel: Vessel) => void;
};

interface UseVesselsParams {
  selectFirstVessel: boolean;
}

const useVessels = (params?: UseVesselsParams): UseVesselReturnType => {
  const { selectFirstVessel } = params || {};
  const dispatch = useAppDispatch();

  const [initialLoad, setInitialLoad] = useState(false);

  const vessels = useSelector(selectSelectAllVessel);
  const selectedVessel = useSelector(selectSelectedVessel);
  const isLoggedOn = useSelector(selectedFloatPlanLoggedOn);
  const isSelectedLoggedOnVesselInvalid = useSelector(
    selectIsSelectedLoggedOnVesselInvalid,
  );

  // Callback to set the selected vessel along with appropriate event tracking
  const selectVessel = useCallback(
    (vessel: Vessel) => {
      captureEvent({
        type: 'vessel: select vessel',
        payload: {
          vessel,
        },
        sendToAmplitude: true,
      });
      dispatch(setSelectedVessel(vessel.id));
    },
    [dispatch],
  );

  // Auto-select the first vessel when there are vessels and the initial load is complete
  useEffect(() => {
    if (selectFirstVessel && vessels.length > 0 && !selectedVessel) {
      selectVessel(vessels[0]);
    }
  }, [selectFirstVessel, vessels, selectedVessel, selectVessel]);

  // Function to fetch and update the vessels. Used both in the mount effect and returned as a helper from the hook.
  const fetchVessels = useCallback(async () => {
    await dispatch(fetchVesselsForCurrentMember()).unwrap();
    setInitialLoad(true);
  }, [dispatch]);

  // Fetch vessels on mount
  useEffect(() => {
    fetchVessels();
  }, [fetchVessels]);

  return {
    vessels,
    initialLoad,
    isLoggedOn,
    isSelectedLoggedOnVesselInvalid,
    selectedVessel,
    fetchVessels,
    selectVessel,
  };
};

export default useVessels;
